
import styled from 'styled-components'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getHomePageMapLoadData, getHomePageMapShipmentData } from '../../features/topPagesMap/topPagesMapSlice'
import { GoogleMap } from '@react-google-maps/api'
import { useSelector } from 'react-redux'
import loadImg from "../../images/svg/top/dark/list-dark.svg"
import yuklemedeImg from "../../images/svg/top/dark/as.svg"
import seferdeImg from "../../images/svg/top/dark/truck-dark.svg"
import bosaltmada from "../../images/svg/top/dark/cek-dark.svg"
import MemoizedOverlayHomepage from '../../components/topPagesMap/overlays/MemoizedOverlayHomepage'
import avatar from "../../images/img/avatar.jpg"
import { HiArrowUturnRight } from "react-icons/hi2";
import { MdLock } from "react-icons/md";

const HOME_MAP_LOCK_KEY = "homeMapLockedView";
const DEFAULT_CENTER = { lat: 39.419221, lng: 34.343262 };
const DEFAULT_ZOOM = 6;

const readLockedView = () => {
  try {
    const value = JSON.parse(localStorage.getItem(HOME_MAP_LOCK_KEY));
    const lat = Number(value?.center?.lat);
    const lng = Number(value?.center?.lng);
    const zoom = Number(value?.zoom);
    if (value?.isLocked && Number.isFinite(lat) && Number.isFinite(lng) && Number.isFinite(zoom)) {
      return {isLocked: true, center: {lat, lng}, zoom};
    }
  } catch {
    // Geçersiz veya erişilemeyen storage değeri varsayılan görünümü etkilemez.
  }
  return null;
};

const getCoordinate = (value) => Number.parseFloat(String(value ?? "").replace(",", "."));
const Container = styled.div`
width: 100%;
height: 100%;
overflow: hidden;

  .wrapper{
    position: relative;
    width: 100%;
    height: 100%;
  }

  .map-controls {
    position: absolute;
    top: 12px;
    right: 52px;
    z-index: 5;
    display: flex;
    gap: 8px;
  }

  .map-control {
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(0, 0, 0, .18);
    border-radius: 8px;
    background: #fff;
    color: #3d3d3b;
    box-shadow: 0 2px 7px rgba(0, 0, 0, .25);
    cursor: pointer;
    font-size: 23px;
    transition: background-color .2s, color .2s, opacity .2s, transform .2s;
  }

  .map-control:hover:not(:disabled) { transform: translateY(-1px); }
  .map-control.active { background: #E85446; color: #fff; border-color: #E85446; }
  .map-control:disabled { opacity: .45; cursor: not-allowed; }
`

const Home = () => {

  const dispatch = useDispatch()
  const storedViewRef = useRef(readLockedView());
  const mapRef = useRef(null);
  const [isMapLocked, setIsMapLocked] = useState(Boolean(storedViewRef.current?.isLocked));
  const [mapCenter, setMapCenter] = useState(storedViewRef.current?.center ?? DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(storedViewRef.current?.zoom ?? DEFAULT_ZOOM);

  const {homePageLocationData} = useSelector(state => state.topPagesMap)
  useEffect(() => {
    dispatch(getHomePageMapShipmentData())
    dispatch(getHomePageMapLoadData())
  },[dispatch])

  const visibleLocations = useMemo(() => [
    ...homePageLocationData.loadLocationList,
    ...homePageLocationData.yuklemedeLocationList,
    ...homePageLocationData.seferdeLocationList,
    ...homePageLocationData.bosaltmadaLocationList,
  ].map(item => ({lat: getCoordinate(item?.lat), lng: getCoordinate(item?.lng)}))
    .filter(position => Number.isFinite(position.lat) && Number.isFinite(position.lng)), [homePageLocationData]);

  const storeLockedView = useCallback((map) => {
    const center = map?.getCenter()?.toJSON();
    const zoom = map?.getZoom();
    if (!center || !Number.isFinite(zoom)) return;

    const view = {isLocked: true, center, zoom};
    setMapCenter(center);
    setMapZoom(zoom);
    try {
      localStorage.setItem(HOME_MAP_LOCK_KEY, JSON.stringify(view));
    } catch {
      // Storage kapalıysa kilit mevcut sayfa oturumunda çalışmaya devam eder.
    }
  }, []);

  const toggleMapLock = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    if (isMapLocked) {
      setIsMapLocked(false);
      try {
        localStorage.removeItem(HOME_MAP_LOCK_KEY);
      } catch {
        // Storage erişimi kilidin açılmasını engellemez.
      }
      return;
    }

    storeLockedView(map);
    setIsMapLocked(true);
  }, [isMapLocked, storeLockedView]);

  const showAllLocations = useCallback(() => {
    const map = mapRef.current;
    if (!map || visibleLocations.length === 0) return;

    if (visibleLocations.length === 1) {
      map.setCenter(visibleLocations[0]);
      map.setZoom(17);
      if (isMapLocked) storeLockedView(map);
      return;
    }

    if (isMapLocked) {
      window.google.maps.event.addListenerOnce(map, "idle", () => storeLockedView(map));
    }
    const bounds = new window.google.maps.LatLngBounds();
    visibleLocations.forEach(position => bounds.extend(position));
    map.fitBounds(bounds, 60);
  }, [isMapLocked, storeLockedView, visibleLocations]);

  const mapOptions = useMemo(() => ({
    gestureHandling: isMapLocked ? "none" : "auto",
    draggable: !isMapLocked,
    scrollwheel: !isMapLocked,
    disableDoubleClickZoom: isMapLocked,
    keyboardShortcuts: !isMapLocked,
    zoomControl: !isMapLocked,
    streetViewControl: !isMapLocked,
  }), [isMapLocked]);




  
  return (
    <Container>
      <div className="wrapper">
        <div className="map-controls">
          <button
            type="button"
            className={`map-control ${isMapLocked ? "active" : ""}`}
            onClick={toggleMapLock}
            aria-label={isMapLocked ? "Harita kilidini aç" : "Haritayı kilitle"}
            aria-pressed={isMapLocked}
            title={isMapLocked ? "Harita kilidini aç" : "Haritayı kilitle"}
          >
            <MdLock />
          </button>
          <button
            type="button"
            className="map-control"
            onClick={showAllLocations}
            disabled={visibleLocations.length === 0}
            aria-label="Tüm konumları göster"
            title="Tüm konumları göster"
          >
            <HiArrowUturnRight />
          </button>
        </div>
        <GoogleMap
          center={mapCenter}
          zoom={mapZoom}
          mapContainerStyle={{width: "100%", height: "100%"}}
          options={mapOptions}
          onLoad={(map) => { mapRef.current = map; }}
          onUnmount={() => { mapRef.current = null; }}
        >

          {
            homePageLocationData.loadLocationList.map((item, index) =>{
              return(
              item?.lat && item?.lng && <MemoizedOverlayHomepage
                key={`load-${item.id ?? index}`}
                position={{ lat: parseFloat(item.lat.replace(",", ".")), lng: parseFloat(item.lng.replace(",", ".")) }}
                userImg={loadImg}
                isAnimated={false}
                type={"load"}
              />
            )})
          }
          {
            homePageLocationData.yuklemedeLocationList.map((item, index) =>(
              item?.lat && item?.lng && <MemoizedOverlayHomepage
                key={`yuklemede-${item.id ?? index}`}
                position={{ lat: parseFloat(item.lat), lng: parseFloat(item.lng) }}
                typeImg={yuklemedeImg}
                userImg={item.driverPhoto ? `https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/Images/${item.driverPhoto}` : avatar}
                isAnimated={false}
                type= {"yuklemede"}
              />
            ))
          }

          {
            homePageLocationData.seferdeLocationList.map((item, index) =>(
              item?.lat && item?.lng && <MemoizedOverlayHomepage
                key={`seferde-${item.id ?? index}`}
                position={{ lat: parseFloat(item.lat), lng: parseFloat(item.lng) }}
                typeImg={seferdeImg}
                isAnimated={true}
                userImg={item.driverPhoto ? `https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/Images/${item.driverPhoto}` : avatar}
                type= {"seferde"}
              />
              ))
          }

          {
            homePageLocationData.bosaltmadaLocationList.map((item, index) =>(
              item?.lat && item?.lng && <MemoizedOverlayHomepage
                key={`bosaltmada-${item.id ?? index}`}
                position={{ lat: parseFloat(item.lat), lng: parseFloat(item.lng) }}
                typeImg={bosaltmada}
                userImg={item.driverPhoto ? `https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/Images/${item.driverPhoto}` : avatar}
                isAnimated={false}
                type= {"bosaltmada"}
              />
            ))
          }
        </GoogleMap>
      </div>
    </Container>
  )
}

export default Home




































// import styled from 'styled-components'
// import Btn from '../../components/shared/button/Btn'
// import InputSelect from '../../components/shared/input/new/InputSelect'
// import { useState, useEffect } from 'react'
// import InputText from '../../components/shared/input/new/InputText'
// import InputPhoneFormat from '../../components/shared/input/new/InputPhoneFormat'
// import InputTextarea from '../../components/shared/input/new/InputTextarea'
// const Container = styled.div`
//   width: 100%;
//   .wrapper{
//     width: 100%;
//     padding: 30px;
//     display: flex;
//     gap: 40px;

//     .input-section{
//       display: flex;
//       flex-direction: column;
//       gap: 20px;
//     }

//     #ege-imza-section{
//       font-family: "Poppins", sans-serif;
//       height: max-content;


//       .img-wrapper{
//         padding: 20px;
//         border-right: 2px solid #E85546;

//         padding-left: 0;
//         text-align: center;

//         .egeYurt-img{
//           text-align: center;
//           padding-bottom: 3px;
//         }

//         .egeyurt-phone{
//           font-size: 12px;
//           font-weight: 600;
//           text-align: center;
//           color: #162259;
//           padding-bottom: 3px;

//         }

//         .egeyurt-social-grp{
//           text-align: center;

//           img{
//             padding: 2px;
//             width: 18px;
//             height: 18px;
//           }

//         }
//       }

//       .top-container{
//         padding-left: 20px;
//         padding-bottom: 10px;

//         .egeyurt-name{
//           font-size: 14px;
//           font-weight: 700;
//           color: #162259;
//           text-transform: uppercase;
//         }

//         .egeyurt-unvan{
//           font-size: 10px;
//           font-weight: 900px;
//           color: #000;
//         }
//       }

//       .mid-container{
//         padding-left: 20px;

//         .grp{
//           padding-bottom: 5px;
//           .icon{
//             display: inline-block;
//           }
          
//           .text{
//             font-size: 12px;
//             font-weight: 500;
//             padding-left: 4px;
//           }
//         }


//       }

//   }

//   #signature{
//     font-family: "Poppins", sans-serif;
//     height: max-content;

//     /* //! column 1 */
//     .kamyoon-img-wrapper{
//       padding: 10px;
//       padding-left: 0;
//       text-align: center;

//       .kamyoon-logo{
//         width: 120px;
//       }

//       .kamyoon-social-grp{

//         img{
//           width: 18px;
//           height: 18px;
//           padding: 2px;

//         }
        
//       }
//     }

//     .seperator{

//       div:first-child{
//         width: 1px;
//         border-left: 1px solid #E85546;
//         height: 30%;
//       }

//       div:last-child{
//         width: 1px;
//         border-left: 1px solid #000;
//         height: 70%;
//       }
//     }



//     .kamyoon-item-list{
//       padding: 10px;
//       padding-right: 0;

//       .kamyoon-item{
//         display: flex;
//         align-items: center;
//         height: 30px;

//         .kamyoon-item-icon{
//           width: 20px;
//         }

//         .kamyoon-item-text{
//           display: inline-block;
//           padding-left: 10px;
//           font-size: 12px;
//           font-weight: 500;
//           padding-top: 3px;

//         }
//       }

//       .kamyoon-item-first{
//         display: flex;
//         align-items: center;
//         height: 30px;
//         margin-bottom: 10px;

//         .kamyoon-item-icon{
//           width: 20px;
//         }

//         .kamyoon-item-text-container{
//           font-size: 12px;
//           font-weight: 500;
//         }

//         .kamyoon-item-text{
//           font-size: 10px;
//           font-weight: 500;
//           padding-left: 10px;

//         }
//       }
//     }

//   }

//   } 

// `

// const firmaOptions = [
//   {
//     value: 1,
//     text: "Kamyoon"
//   },
//   {
//     value: 2,
//     text: "Egeyurt"
//   },
// ]

// const Home = () => {

//   const [firmaType, setFirmaType] = useState(  {
//     value: 2,
//     text: "Egeyurt"
//   })

//   const [name, setName] = useState("Abdullah Sağ")
//   const [unvan, setUnvan] = useState("Web Developer")
//   const [phoneNumber, setPhoneNumber] = useState("5061693613")
//   const [mail, setMail] = useState("abdullahegeyurt@gmail.com")
//   const [adres, setAdres] = useState("Altınkum Mah. Yolören 45 Sokak No:11 Edremit/Balıkesir")

//   const [cssId, setCssId] = useState("ege-imza-section")

//   useEffect(() => {
//     if (phoneNumber) {
//         const formattedPhoneNumber = (phoneNumber.startsWith("+90") || phoneNumber.startsWith("90")) ? `${phoneNumber}` : `90${phoneNumber}`;
//         setPhoneNumber(formattedPhoneNumber);
//     }
// }, []);


//   useEffect(() => {
//     setCssId(firmaType.value === 1 ? "signature" : "ege-imza-section")
//   },[firmaType])

//   const handleCopy = () => {
//     const signature = document.getElementById(cssId);
//     const range = document.createRange();
//     range.selectNode(signature);
//     window.getSelection().removeAllRanges();
//     window.getSelection().addRange(range);
//     document.execCommand('copy');
//     window.getSelection().removeAllRanges();
//     alert('Signature copied to clipboard!');
//   };

//   return (
//     <Container>
//       <div className="wrapper">
//         <div className="input-section">
//           <InputSelect data={firmaType.text} setData={setFirmaType} initialOptions={firmaOptions} name={"firmaType"} inputWidth={"280px"}/>
//           <InputText data={name} setData={(e) => setName(e.target.value)} inputWidth={"280px"} name={"name"} labelText='Adı Soyadı'/>
//           <InputText data={unvan} setData={(e) => setUnvan(e.target.value)} inputWidth={"280px"} name={"unvan"} labelText='Unvan'/>
//           <InputPhoneFormat data={phoneNumber} setData={(e) => setPhoneNumber(e.target.value)} inputWidth={"280px"} labelText={"Telefon Numarası"} name={"phoneNumber"}/>
//           <InputText data={mail} setData={(e) => setMail(e.target.value)} inputWidth={"280px"} name={"mail"} labelText='Mail Adresi'/>
//           <InputTextarea data={adres} setData={(e) => setAdres(e.target.value)} inputWidth={"280px"} name={"adres"} labelText="Adres"/>
//         </div>

//       {
//         firmaType.value === 1 ?
//         <div >
//           <table id='signature' cellspacing="0" cellpadding="0" >
//           <tbody>
//             <tr>

//               {/* //! column 1 */}
//               <td>
//                 <div className="kamyoon-img-wrapper">
//                   <img className='kamyoon-logo' src="https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/Group+23328.png" alt="" />
//                   <div className='kamyoon-social-grp'>
//                     <a href="https://www.instagram.com/kamyoonapp/" target='_blank' rel = "noreferrer">
//                       <img src="https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/Vector.png" alt="" />
//                     </a>
//                     <a href="https://www.facebook.com/people/Kamyoon/100089210273146/" target='_blank' rel = "noreferrer">
//                       <img src="https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/Vector-1.png" alt="" />
//                     </a>
//                     <a href="https://www.youtube.com/@kamyoon" target='_blank' rel = "noreferrer">
//                       <img src="https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/Vector-4.png" alt="" />
//                     </a>
//                     <a href="https://www.linkedin.com/company/kamyoon" target='_blank' rel = "noreferrer">
//                       <img src="https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/Clip+path+group.png" alt="" />
//                     </a>
//                     <a href="https://x.com/KamyoonApp" target='_blank' rel = "noreferrer">
//                       <img src="https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/Vector-3.png" alt="" />
//                     </a>
//                     <a href="https://www.tiktok.com/@kamyoonapp" target='_blank' rel = "noreferrer">
//                       <img src="https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/Vector-2.png" alt="" />
//                     </a>
//                   </div>
//                 </div>
//               </td>

//               {/* //! column 2 */}
//               <td className='seperator'>
//                 <div></div>
//                 <div></div>
//               </td>

//               {/* //! column 2 */}
//               <td className='kamyoon-item-list'>
//                 <div className='kamyoon-item-first'>
//                   <img className='kamyoon-item-icon' src="https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/Group+23327.png" style={{width: "22px", height: "22px"}} alt="" />
//                   <div className='kamyoon-item-text-container'>
//                     <div className="kamyoon-item-text">{name}</div>
//                     <div className="kamyoon-item-text">{unvan}</div>
//                   </div>
//                 </div>
//                 <div className='kamyoon-item' style={{paddingBottom: "5px"}}>
//                   <img className='kamyoon-item-icon' style={{float: "left", width: "22px", height: "22px"}} src="https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/Group+23326.png" alt="" />
//                   <div className='kamyoon-item-text' style={{float: "left"}}>{phoneNumber.slice(2)}</div>
//                 </div>
//                 <div className='kamyoon-item' style={{paddingBottom: "5px"}}>
//                   <img className='kamyoon-item-icon' style={{float: "left", width: "22px", height: "22px"}} src="https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/Group+23325.png" alt="" />
//                   <div className='kamyoon-item-text' style={{float: "left"}}>{mail}</div>
//                 </div>
//                 <div className='kamyoon-item' style={{paddingBottom: "5px"}}>
//                   <img className='kamyoon-item-icon' style={{float: "left", width: "22px", height: "22px"}} src="https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/Group+23324.png" alt="" />
//                   <div className='kamyoon-item-text' style={{float: "left"}}>{adres}</div>
//                 </div>
//               </td>
//             </tr>
//           </tbody>
//           </table>
//       </div>
//       :
//       <table id='ege-imza-section' cellspacing="0" cellpadding="0" style={{backgroundColor: "#fff"}}>
//         <tbody>
//           <tr>
//             <td>
//               <div className="img-wrapper">
//                 <img className='egeYurt-img' src="https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/Group+23329.png" width={"100px"} draggable="false" alt="" />
//                 <div className='egeyurt-phone'>444 6 985</div>
//                 <div className='egeyurt-social-grp'>
//                 {/* <a href="https://www.instagram.com/kamyoonapp/" target='_blank'>
//                     <img src="https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/Vector.png" alt="" />
//                   </a>
//                   <a href="https://www.facebook.com/people/Kamyoon/100089210273146/" target='_blank'>
//                     <img src="https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/Vector-1.png" alt="" />
//                   </a>
//                   <a href="https://www.youtube.com/@kamyoon" target='_blank'>
//                     <img src="https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/Vector-4.png" alt="" />
//                   </a>
//                   <a href="https://www.linkedin.com/company/kamyoon" target='_blank'>
//                     <img src="https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/Clip+path+group.png" alt="" />
//                   </a> */}
//                 </div>
//               </div>
//             </td>
//             <td>
//               <div className="top-container">
//                 <div className='egeyurt-name'>{name}</div>
//                 <div className='egeyurt-unvan'>{unvan}</div>
//               </div>
//               <div className="mid-container">
//                 <div className='grp'>
//                     <img className='icon' src={"https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/mobile.png"} style={{width: "8px", color: "#000"}} alt="" />
//                     <span className='text' style={{ color: "#000"}}>{phoneNumber.slice(2)}</span>
//                 </div>
//                 <div className='grp'>
//                   <img className='icon' src={"https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/envelope.png"} style={{width: "10px", color: "#000"}} alt=''/>
//                   <span className='text' style={{ color: "#000"}}>{mail}</span>
//                 </div>
//                 <div className='grp'>
//                   <img className='icon' src={"https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/map.png"} style={{width: "12px", color: "#000"}} alt=''/>
//                   <span className='text' style={{ color: "#000"}}>{adres}</span>
//                 </div>
//                 <div className='grp'>
//                   <img className='icon' src={"https://egeyurtgrupbucket.s3.eu-north-1.amazonaws.com/KamyoonIcons/link.png"} style={{width: "13px", color: "#000"}} alt=''/>
//                   <span className='text' style={{ color: "#000", textDecoration: "none"}}>www.egeyurtgrup.com</span>
//                 </div>
//               </div>
//             </td>
//           </tr>
//         </tbody>
//       </table>
//       }

    

//       <Btn bgColor='#E85446' color='#fff' width={"220px"} handleClick={handleCopy}>Basssss</Btn>
//       </div>

//     </Container>
//   )
// }

// export default Home






// import styled from 'styled-components'
// import PhoneInput from 'react-phone-number-input/input'
// import { useEffect, useState } from 'react'
// import { SmoothCorners } from 'react-smooth-corners'
// import mapDark from "../../images/img/map-dark.png"
// import mapGray from "../../images/img/map-gray.png"
// import mapNormal from "../../images/img/map-normal.png"
// import axios from 'axios'
// import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';

// import polyline from '@mapbox/polyline';
// const containerStyle = {
//   width: '100%',
//   height: '400px'
// };

// const center = {
//   lat: 39.884450,
//   lng: 32.788696
// };

// const Home = () => {

//   const [decodedPath, setDecodedPath] = useState([]);
//   const [asdas1, setAsdas1] = useState()
//   const [asdas2, setAsdas2] = useState()
//   const [asdas3, setAsdas3] = useState()

//   const [markers, setMarkers] = useState([
//     { lat: 39.884450, lng: 32.788696 },
//     { lat: 38.839708, lng: 41.704102 }
//   ]);

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const response = await axios.post("https://routes.googleapis.com/directions/v2:computeRoutes",
//           {
//             "origin": {
//               "location": {
//                 "latLng": {
//                   "latitude": 39.884450,
//                   "longitude": 32.788696
//                 }
//               }
//             },
//             "destination": {
//               "location": {
//                 "latLng": {
//                   "latitude": 37.968290,
//                   "longitude": 34.681091
//                 }
//               }
//             },
//             "computeAlternativeRoutes": true,
//           },
//           {
//             headers: {
//               // 'Content-Type': 'application/json',
//               // 'X-Goog-Api-Key': process.env.REACT_APP_GOOGLE_API_KEY,
//               // 'X-Goog-FieldMask': 'routes.legs.steps.polyline'
//               // "X-Goog-FieldMask": "routes duration, routes.distanceMeters,routes.polyline.encodedPolyline"


//               "Content-Type": "application/json",
//               "X-Goog-Api-Key": process.env.REACT_APP_GOOGLE_API_KEY,
//               "X-Goog-FieldMask": "*",
//             }
//           }
//         );
        
//         const routes = response.data.routes;
//         let completePath = [];
//         console.log(routes);

//         setAsdas1(polyline.decode(routes[0].polyline.encodedPolyline).map(([lat, lng]) => ({ lat, lng })))
//         setAsdas2(polyline.decode(routes[1].polyline.encodedPolyline).map(([lat, lng]) => ({ lat, lng })))
//         setAsdas3(polyline.decode(routes[2].polyline.encodedPolyline).map(([lat, lng]) => ({ lat, lng })))

//         routes.forEach(route => {
//           route.legs.forEach(leg => {
//             leg.steps.forEach(step => {
//               const encodedPolyline = step.polyline.encodedPolyline;
//               const stepDecodedPath = polyline.decode(encodedPolyline).map(([lat, lng]) => ({ lat, lng }));
//               completePath = [...completePath, ...stepDecodedPath];
//             });
//           });
//         });

//         setDecodedPath(completePath);
        
//       } catch (err) {
//         console.error(err);
//       }
//     }

//     fetchData();
//   }, []);

//   const polylineOptions = {
//     strokeColor: '#FF0000',
//     strokeOpacity: 0.8,
//     strokeWeight: 4,
//     fillColor: '#FF0000',
//     fillOpacity: 0.35,
//     clickable: false,
//     draggable: true,
//     editable: false,
//     visible: true,
//     zIndex: 1,
//     geodesic: true,
//     icons: [
//       {
//         icon: "hello",
//         offset: "0%",
//         repeat: "20px",
//       }
//     ],
//   };

//   console.log(asdas1);
//   console.log(asdas2);
//   console.log(asdas3);

//   const handlePolylineDragEnd = (path) => {
//     const updatedPath = path.latLngs.getArray().map(latLng => ({
//       lat: latLng.lat(),
//       lng: latLng.lng()
//     }));
//     setAsdas1(updatedPath);
//   };

//   const handleDrag = (asdassdasdsa) => {
//     console.log(asdassdasdsa);
//   } 

//   return (
//       <GoogleMap
//         id="route-map"
//         mapContainerStyle={{ height: "400px", width: "800px" }}
//         zoom={14}
//         center={{ lat: 39.884450, lng: 32.7886966 }}
//       >
//         {
//           <Marker options={{
//             draggable: true
//           }} position={{ lat: 39.884450, lng: 32.788696 }} onDragEnd={handleDrag} />  
//         }
//          {asdas1 &&
//           <Polyline
//             path={asdas1}
//             options={polylineOptions}
//             onDragEnd={(e) => handlePolylineDragEnd(e)}
//           />
//         }

//       </GoogleMap>
//   )
// }

// export default Home
